import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Outlet, useLocation, useMatches } from "react-router-dom";
import i18n from 'i18next';
import { ConfigProvider } from "antd";

import NavigationBar from "../common/NavigationBar";
import LoadingOverlay from "./LoadingOverlay";
import useApplyStore from "../../store/useApplyStore";
import { basename } from '../../setting';
import useViewStore from "../../store/useViewStore";

function Root() {
  const { step } = useApplyStore();
  const location = useLocation();
  const { isPadView, isMobileView, setView } = useViewStore((state) => state);
  const [isHomePage, setIsHomePage] = useState(location.pathname === `/${basename}/${i18n.language}`);
  const [isApplyPage, setIsApplyPage] = useState(location.pathname === `/${basename}/${i18n.language}/apply`);
  const [isApplySupplementaryPage, setIsApplySupplementaryPage] = useState(location.pathname.startsWith(`/${basename}/${i18n.language}/apply/supplementary`));
  const [isApplicationProgressPage, setIsApplicationProgressPage] = useState(location.pathname === `/${basename}/${i18n.language}/apply/progress`);
  const [isAdminPage, setIsAdminPage] = useState(location.pathname.startsWith(`/${basename}/${i18n.language}/admin`));
  const matches = useMatches();
  const [isPageNotFound, setIsPageNotFound] = useState(Object.keys(matches[0].params)[0] === "*");

  useEffect(() => {
    setIsHomePage(location.pathname === `/${basename}/${i18n.language}` || location.pathname === `/${basename}/${i18n.language}/`);
    setIsAdminPage(location.pathname.startsWith(`/${basename}/${i18n.language}/admin`));
    setIsApplicationProgressPage(location.pathname === `/${basename}/${i18n.language}/apply/progress`);
    setIsPageNotFound(Object.keys(matches[0].params)[0] === "*");
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      setView();
    };

    window.addEventListener('resize', handleResize);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [setView]);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorBorder: "var(--aia-bx-gray-5)",
          colorPrimary: "#4096ff",
          colorPrimaryHover: "var(--aia-bx-blue-2)"
        },
        components: {
          Menu: {
            itemBg: "transparent",
            itemPaddingInline: 0,
            horizontalItemHoverColor: "var(--aia-bx-blue-2)",
            horizontalItemSelectedColor: "var(--aia-bx-blue-2)",
            horizontalItemHoverBg: "var(--aia-bx-grey-20)",
          },
          Select: {
            optionFontSize: 16,
            singleItemHeightLG: 46
          },
        },
      }}
    >
      <LoadingOverlay>
        <MainContainer
          $isHomePage={isHomePage}
          $isApplyPage={isApplyPage}
          $isAdminPage={isAdminPage}
          $isApplySupplementaryPage={isApplySupplementaryPage}
          $isApplicationProgressPage={isApplicationProgressPage}
        >
          {
            !(isHomePage || isAdminPage || isApplicationProgressPage || isPageNotFound) &&
            <NavigationBar />
          }

          <Outlet />
        </MainContainer>
      </LoadingOverlay>
    </ConfigProvider>
  );
}

const MainContainer = styled.main`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 100%;
  width: 100%;
  ${props => props.$isAdminPage && "height: 100%;"};

  ${props => (props.$isHomePage || props.$isAdminPage) && `
    background-color: var(--aia-bx-gray-6);
  `}

  ${props => props.$isApplyPage && `
    background-color: var(--aia-bx-gray-10);
  `}

  ${props => (props.$isApplySupplementaryPage || props.$isApplicationProgressPage) && `
    background-color: var(--aia-bx-bright-blue-5);
  `}
  
  align-items: center;
`;

export default Root;